/**
 * tutorInvoicesViewModel — ce que « Mes factures » lit dans le store.
 *
 * Patron A : les factures rangées par le réducteur `tutors` (la première page remplace la liste, les
 * suivantes s'y ajoutent) et la tranche de leur lecture. `answered` vient de l'écran
 * (`useTutorInvoicesData`) : tant que la première page de CETTE ouverture n'est pas arrivée, la liste
 * gardée d'une visite précédente ne s'affiche pas — ni elle, ni un « Aucune facture » d'avant la
 * première lecture.
 */
import { createSelector } from "reselect"

import {
    toTutorInvoiceLinks,
    toUITutorInvoices,
    type ApiTutoringInvoice,
    type TutorInvoiceLinks,
} from "../../../adapters/tutor/Account/tutorInvoiceAdapter"
import { type UITutorInvoice } from "../../../ui/types/tutorCompte"

export type TutorInvoicesViewModel = {
    /** `null` tant que la première page n'est pas là. */
    invoices: UITutorInvoice[] | null
    /** Les adresses du PDF et du récapitulatif de chaque facture, par id. */
    links: Record<number, TutorInvoiceLinks>
    loading: boolean
    loadingMore: boolean
    error: unknown
}

// Le store est en JS non typé : on lit les champs qu'on connaît, sans le décrire en entier.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

const selectRows = (state: ReduxState): ApiTutoringInvoice[] | undefined => state?.data?.tutors?.tutoringInvoices
const selectRequesting = (state: ReduxState): boolean => !!state?.api?.getTutoringInvoices?.request
const selectError = (state: ReduxState): unknown => state?.api?.getTutoringInvoices?.error ?? null
const selectAnswered = (_state: ReduxState, answered: boolean) => answered

export const tutorInvoicesViewModel = createSelector(
    [selectRows, selectRequesting, selectError, selectAnswered],
    (rows, requesting, error, answered): TutorInvoicesViewModel => ({
        invoices: answered ? toUITutorInvoices(rows) : null,
        links: answered ? toTutorInvoiceLinks(rows) : {},
        loading: !answered && error == null,
        loadingMore: answered && requesting,
        // Une suite qui échoue garde la liste : seule la première page fait une page d'erreur.
        error: answered ? null : error,
    })
)
